import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { CartActions } from '@spartacus/cart/base/core';
import { Cart, PromotionLocation } from '@spartacus/cart/base/root';
import { CmsService, Promotion } from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { delay, map, startWith, switchMap, take } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { B2bUserService } from 'src/app/service/b2buser/b2b-user.service';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { CartExtendService } from 'src/app/service/cart/cart-extend.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { FREIGHT, SnahCountryService } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';

@Component({
  selector: 'cx-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AsyncPipe],
  standalone: false,
})
export class OrderSummaryComponent implements OnInit, OnDestroy {
  @Input()
  cart: Cart;
  @Input()
  pageId: string;
  @Input()
  promotions: Promotion[];
  @Input() promotionLocation: PromotionLocation;
  showSrp = false;
  srpSubscription$: Subscription;
  currentPageSubscription$: Subscription;
  freightLevelSubsciption$: Subscription;
  isCheckoutPage = false;
  taxLabel: string;
  cartTotalPriceValue: any;
  freightPrice = '$0';
  deliveryModeSubsciption$: Subscription;
  isCheckoutPageSnah = false;
  selectedDelivCode = '';
  getCart: any;
  loadCartSuccessSubscription$: Subscription;

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();
  isExternalTaxEnabled$: Observable<boolean> = this.baseStoreService
    .getBasestoreData()
    .pipe(map((response: { externalTaxEnabled: boolean }) => response.externalTaxEnabled));

  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  showDeliveryFee$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.DELIVERY_FEE)
    )
  );

  showDeliveryFeeCustomMsg$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.DELIVERY_FEE_CUSTOM_MSG)
    ),
    startWith(null)
  );

  showGstMsg$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      combineLatest([
        this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_MSG),
        this.hideGstMsgInCheckout$,
      ])
    ),
    map(([isGstFeatureEnabled, hideGstMsgInCheckout]) => isGstFeatureEnabled && !hideGstMsgInCheckout)
  );

  hideSalesTaxInCart$: Observable<boolean> = combineLatest([
    this.cmsService.getCurrentPage().pipe(map((currentPage) => currentPage && currentPage.label.includes('/cart'))),
    this.isExternalTaxEnabled$,
  ]).pipe(map(([isCartPage, isExternalTaxEnabled]) => isCartPage && isExternalTaxEnabled));

  hideGstMsgInCheckout$: Observable<boolean> = combineLatest([
    this.cmsService.getCurrentPage().pipe(map((currentPage) => currentPage && currentPage.label.includes('/checkout'))),
    this.isExternalTaxEnabled$,
  ]).pipe(map(([isCheckoutPage, isExternalTaxEnabled]) => isCheckoutPage && isExternalTaxEnabled));

  showSalesTax$: Observable<boolean> = this.hideSalesTaxInCart$.pipe(
    map((hideSalesTaxInCart) => this.cart.net && !hideSalesTaxInCart)
  );

  isCustomTranslationsEnabled$(customKeys: string[]): Observable<boolean> {
    return combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
      switchMap(([activeCountry, activeLanguage]) =>
        this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, customKeys)
      )
    );
  }

  isOrderSummaryRelatedCustomTranslationsEnabled$: Observable<boolean> = this.isCustomTranslationsEnabled$([
    'cart.orderSummary.salesTax',
    'cart.orderSummary.subtotal',
    'cart.orderSummary.total',
  ]);

  isDiscountRelatedCustomTranslationsEnabled$: Observable<boolean> = this.isCustomTranslationsEnabled$([
    'cart.orderSummary.discount',
  ]);

  showActiveSnahCountry$: Observable<boolean> = this.SnahCountryService.getSnahFeatures(FREIGHT);

  constructor(
    private componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private cmsService: CmsService,
    private gtmService: GoogleTagManagerService,
    private countryService: ShimanoCountryService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private languageService: ShimanoLanguageService,
    private customTranslationService: CustomTranslationService,
    private breakpointService: BreakpointService,
    private currentBusinesstype: CurrentBusinessType,
    private storageService: StorageService,
    private checkoutService: CheckoutServiceExtends,
    private cartExtendService: CartExtendService,
    private b2bUserService: B2bUserService,
    private SnahCountryService: SnahCountryService,
    private baseStoreService: BaseStoreService,
    private actionsSubject: ActionsSubject
  ) {}

  ngOnInit() {
    this.getInitSrpToggleValue();
    this.getTotalPrice();
    this.freightLevelSubsciption$ = this.componentService.freightLevel$.subscribe((data) => {
      this.freightPrice = data;
      this.cdr.detectChanges();
    });
    this.deliveryModeSubsciption$ = this.componentService.deliveryModeCode$.subscribe((data) => {
      this.selectedDelivCode = data;
      this.cdr.detectChanges();
    });
    this.currentPageSubscription$ = this.cmsService.getCurrentPage().subscribe((currentPageDetails) => {
      if (currentPageDetails && currentPageDetails.template.includes('ShimanoCheckoutSummaryPageTemplate')) {
        this.isCheckoutPageSnah = true;
      } else {
        this.isCheckoutPageSnah = false;
      }
      if (currentPageDetails && currentPageDetails.label.includes('/checkout')) {
        this.isCheckoutPage = true;
      } else {
        this.isCheckoutPage = false;
      }
      this.cdr.detectChanges();
    });

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    if (this.cart.appliedProductPromotions.length && !this.isCheckoutPage) {
      this.gtmPromoView(this.cart);
    }
    this.getCart = this.componentService.checkoutCartEntries$.getValue();

    if (this.isCheckoutPage) {
      this.watchCartAPIStatus();
    }
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  getTotalPrice() {
    this.cartTotalPriceValue = this.cart.totalPriceWithTax?.value;
    this.checkoutService.updateTotalSummary(this.cartTotalPriceValue);
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  gtmPromoView(cart: any) {
    if (cart.appliedProductPromotions.length) {
      cart.appliedProductPromotions.forEach((promo: any) => {
        if (promo && promo.consumedEntries.length) {
          promo.consumedEntries.forEach((consumedEntry: any) => {
            cart.entries.forEach((entry: any) => {
              if (consumedEntry.orderEntryNumber == entry.entryNumber) {
                this.gtmService.pushTag({
                  ecommerce: null,
                  search_term: '',
                  results_page: '',
                  affiliation: '',
                  content_type: '',
                  content_id: '',
                  content_name: '',
                });
                this.gtmService.pushTag({
                  event: GoogleTagManagerEvent.PROMO_VIEW,
                  ecommerce: {
                    promotion_id: promo.promotion.code,
                    promotion_name: promo.description,
                    creative_slot: this.pageId,
                    items: [
                      {
                        item_id: entry.product.code,
                        item_name: entry.product.name,
                        affiliation: this.affiliation,
                        index: entry.entryNumber,
                        item_brand: entry?.product?.brand?.name,
                        price: entry.basePrice.formattedValue,
                      },
                    ],
                  },
                });
              }
            });
          });
        }
      });
    }
  }

  get taxLabel$(): Observable<string> {
    return this.countryService.getActive().pipe(
      take(1),
      map((activeCountry) => (activeCountry === 'sg' ? activeCountry : 'default'))
    );
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointService.breakpoint$.pipe(map((breakpoint) => breakpoint === BREAKPOINT.xs));
  }

  hasFreeFreight(freight: boolean, freeDeliveryModes: any): boolean {
    let hasFreeDelivery: boolean;
    if (freeDeliveryModes !== undefined && this.isCheckoutPageSnah) {
      if (freeDeliveryModes.find((delivMode: any) => delivMode.code === this.selectedDelivCode)) {
        hasFreeDelivery = true;
      } else {
        hasFreeDelivery = false;
      }
    } else {
      hasFreeDelivery = freight;
    }
    this.componentService.sethasFreeFreight(freight);
    return hasFreeDelivery;
  }

  watchCartAPIStatus(): void {
    this.loadCartSuccessSubscription$ = this.actionsSubject
      .pipe(ofType(CartActions.LOAD_CART_SUCCESS), delay(0))
      .subscribe((cartAPIreload: any) => {
        if (cartAPIreload) {
          this.componentService.checkoutCartEntries$.next(this.cart); // Update on cart API reload
          this.getCart = this.componentService.checkoutCartEntries$.getValue();
        }
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
    if (this.freightLevelSubsciption$) {
      this.freightLevelSubsciption$.unsubscribe();
    }
    if (this.deliveryModeSubsciption$) {
      this.deliveryModeSubsciption$.unsubscribe();
    }
    this.loadCartSuccessSubscription$?.unsubscribe();
  }
}
